import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Alert, Checkbox, ConfigProvider, Modal } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import AuthService from '../../services/authService';
import './Login.css';

const { Title, Text } = Typography;

const KAKAO_REST_API_KEY = '7aa3f7011cc019440d5829cb5d82ba4e';

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [domainModal, setDomainModal] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (window.location.hostname.includes('adhelp.store')) {
            setDomainModal(true);
        }
    }, []);

    useEffect(() => {
        if (location.state?.error) {
            setError(location.state.error);
            window.history.replaceState({}, '');
        }
    }, [location.state]);

    const handleKakaoLogin = () => {
        const redirectUri = `${window.location.origin}/auth/kakao/callback`;
        window.location.href = `https://kauth.kakao.com/oauth/authorize?client_id=${KAKAO_REST_API_KEY}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code`;
    };

    /** 토큰이 있으면 홈으로만 이동. 서버 검증(세션·구독 등)은 메인 레이아웃 진입 시 수행 */
    useEffect(() => {
        if (AuthService.isUsableToken()) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    const onFinish = async values => {
        const { userId, password } = values;
        setError('');
        setLoading(true);

        try {
            const response = await AuthService.login(userId, password);
            if (response) {
                navigate('/');
            } else {
                setError('로그인에 실패했습니다.');
            }
        } catch (err) {
            setError(err.message || '로그인 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Modal
            open={domainModal}
            onCancel={() => setDomainModal(false)}
            footer={[
                <Button key="go" type="primary" danger onClick={() => { window.location.href = 'https://adcare.kr'; }}>
                    adcare.kr 바로가기
                </Button>,
                <Button key="close" onClick={() => setDomainModal(false)}>닫기</Button>,
            ]}
            title="🔔 서비스 도메인이 변경되었습니다"
            width={420}
        >
            <div style={{ padding: '8px 0', lineHeight: 1.8 }}>
                <p style={{ marginBottom: 12 }}>
                    ADHELP 서비스가 <b>ADCARE</b>로 새롭게 시작합니다.
                </p>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>기존 주소</div>
                    <div style={{ fontSize: 14, textDecoration: 'line-through', color: '#94a3b8' }}>adhelp.store</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, marginBottom: 4 }}>새 주소</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>adcare.kr</div>
                </div>
                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                    북마크를 새 주소로 변경해 주시면 계속 이용하실 수 있습니다.
                </p>
            </div>
        </Modal>
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#2e5bff',
                    borderRadius: 8,
                },
            }}
        >
            <div
                className="login-page"
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                }}
            >
                <Card
                    bordered={false}
                    style={{
                        width: '100%',
                        maxWidth: 400,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
                        borderRadius: '16px',
                    }}
                    bodyStyle={{ padding: '40px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <Title
                            level={2}
                            style={{
                                color: '#2e5bff',
                                fontWeight: 900,
                                margin: 0,
                                letterSpacing: '-1px',
                            }}
                        >
                            ADCARE
                        </Title>
                        <Text type="secondary">전문가용 광고 순위 관리 솔루션</Text>
                    </div>

                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: '24px' }}
                        />
                    )}

                    <Form
                        name="login_form"
                        initialValues={{ remember: true }}
                        onFinish={onFinish}
                        layout="vertical"
                        size="large"
                    >
                        <Form.Item
                            name="userId"
                            rules={[{ required: true, message: '아이디를 입력해주세요.' }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="아이디"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="비밀번호"
                            />
                        </Form.Item>

                        <Form.Item name="remember" valuePropName="checked" noStyle>
                            <Checkbox style={{ marginBottom: '24px' }}>
                                로그인 정보 기억하기
                            </Checkbox>
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                icon={<LoginOutlined />}
                                style={{ height: '62px', fontSize: '16px', fontWeight: 600 }}
                            >
                                {loading ? '로그인 처리 중...' : '로그인'}
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{ display: 'flex', alignItems: 'center', margin: '8px 0 20px' }}>
                        <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                        <Text type="secondary" style={{ margin: '0 12px', fontSize: 12 }}>
                            또는
                        </Text>
                        <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                    </div>

                    <button
                        onClick={handleKakaoLogin}
                        style={{
                            width: '100%',
                            height: 54,
                            background: '#FEE500',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#3C1E1E',
                        }}
                    >
                        <img
                            src="https://developers.kakao.com/assets/img/about/logos/kakaolink/kakaolink_btn_medium.png"
                            alt="kakao"
                            style={{ width: 32, height: 32 }}
                        />
                        카카오로 로그인
                    </button>
                    <p style={{ marginTop: 10, fontSize: 12, color: '#888', textAlign: 'center' }}>
                        신규 고객 또는 계정이 없는 고객은 카카오로 로그인이 가능합니다.
                    </p>
                </Card>
            </div>
        </ConfigProvider>
        </>
    );
};

export { Login };
